import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  getUserAccount,
  getAccountMembers,
  checkUsageLimits
} from '../services/accountService';
import {
  migrateUserComplete,
  needsMigration
} from '../services/accountMigrationService';

const AccountContext = createContext();

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}

export function AccountProvider({ children }) {
  const { currentUser } = useAuth();
  const [account, setAccount] = useState(null);
  const [accountMembers, setAccountMembers] = useState([]);
  const [usageLimits, setUsageLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load account data when user changes
  useEffect(() => {
    if (!currentUser) {
      setAccount(null);
      setAccountMembers([]);
      setUsageLimits(null);
      setLoading(false);
      return;
    }

    loadAccountData();
  }, [currentUser]);

  const loadAccountData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user needs migration
      const shouldMigrate = await needsMigration(currentUser.uid);

      if (shouldMigrate) {
        console.log('Migrating user to account structure...');
        await migrateUserComplete(currentUser.uid);
      }

      // Load account data
      const userAccount = await getUserAccount(currentUser.uid);

      if (!userAccount) {
        throw new Error('No account found for user');
      }

      setAccount(userAccount);

      // Load account members
      const members = await getAccountMembers(userAccount.id);
      setAccountMembers(members);

      // Load usage limits
      const limits = await checkUsageLimits(userAccount.id);
      setUsageLimits(limits);

    } catch (err) {
      console.error('Error loading account data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshAccount = async () => {
    if (!currentUser) return;
    await loadAccountData();
  };

  const isAccountOwner = () => {
    if (!account || !currentUser) return false;
    return account.ownerId === currentUser.uid;
  };

  const isAccountMember = (userId) => {
    if (!account) return false;
    return account.memberIds.includes(userId);
  };

  const hasUnlimitedGenerations = () => {
    return usageLimits?.hasUnlimitedGenerations || false;
  };

  const hasUnlimitedScheduledPosts = () => {
    return usageLimits?.hasUnlimitedScheduledPosts || false;
  };

  const isPremiumAccount = () => {
    return account?.accountType === 'premium';
  };

  const value = {
    account,
    accountMembers,
    usageLimits,
    loading,
    error,
    refreshAccount,
    isAccountOwner,
    isAccountMember,
    hasUnlimitedGenerations,
    hasUnlimitedScheduledPosts,
    isPremiumAccount
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}
